import React, { useState } from "react";
import { PROFILE } from "../../mock";
import { Phone, MapPin, Building2, Send } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { SectionTitle } from "./About";
import { submitMessage, errorMessage } from "../../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSending(true);
    try {
      await submitMessage(form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Message received — we will respond shortly.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not send message — please try again"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="scroll-smooth-section py-24 bg-cream/40">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTitle
          kicker="Reach Out"
          title="Get in touch"
          subtitle="For consultations, lectures, book releases, or charitable engagements — a message will reach Dr. Shaji's office."
        />

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          {/* Info */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-emerald-deep text-ivory rounded-2xl p-6">
              <Building2 className="w-6 h-6 text-gold" />
              <div className="text-xs uppercase tracking-widest text-gold mt-3">Official</div>
              <div className="font-display text-lg mt-1">Senior Consultant Neurologist</div>
              <div className="text-ivory/80 text-sm">Aaraamam, Pongumoodu, TVPM</div>
            </div>
            <div className="bg-ivory rounded-2xl p-6 border border-stone-200/60">
              <MapPin className="w-6 h-6 text-emerald-deep" />
              <div className="text-xs uppercase tracking-widest text-gold mt-3">Residence</div>
              <p className="text-stone-700 mt-1 leading-relaxed">{PROFILE.address}</p>
            </div>
            <div className="bg-ivory rounded-2xl p-6 border border-stone-200/60">
              <Phone className="w-6 h-6 text-emerald-deep" />
              <div className="text-xs uppercase tracking-widest text-gold mt-3">Telephone</div>
              <ul className="mt-2 grid sm:grid-cols-2 gap-1">
                {PROFILE.phones.map(p => (
                  <li key={p}><a href={`tel:${p.replace(/\s/g, "")}`} className="text-stone-700 hover:text-emerald-deep text-sm">{p}</a></li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 bg-ivory rounded-2xl p-7 border border-stone-200/60 shadow-sm space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-600">Name</label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 bg-cream/40 border-stone-200" placeholder="Your full name" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-600">Email</label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 bg-cream/40 border-stone-200" placeholder="you@example.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-600">Phone (optional)</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 bg-cream/40 border-stone-200" placeholder="+91 …" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-600">Message</label>
              <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1.5 min-h-[140px] bg-cream/40 border-stone-200" placeholder="Tell us how we can help…" data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={sending} data-testid="contact-submit-btn" className="bg-emerald-deep hover:bg-[#1F2A3F] text-ivory rounded-full h-12 px-7">
              <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
